/**
 * LiveKit Provider Implementation.
 *
 * Animation data is extracted with insertable streams: the provider installs
 * an `RTCRtpScriptTransform` worker on animation-candidate video receivers
 * (early, right after `setRemoteDescription`; see early-transform.ts). The
 * worker consumes frames that match the animation protocol and passes
 * everything else through untouched.
 *
 * @packageDocumentation
 */

import { BaseProvider } from '../base/BaseProvider';
import type {
  RTCConnectionConfig,
  RTCPrepareConnectionConfig,
  LiveKitConnectionConfig,
  TransportStats,
} from '../../types';
import { isLiveKitConfig, isLiveKitPrepareConfig } from '../../types';
import type {
  AnimationTrackCallbacks,
  AudioTrackCallbacks,
} from '../../core/types';
import { logger, trackEvent } from '../../utils';
import {
  findLiveKitTrackSid,
  registerEarlyReceiverHandler,
} from './early-transform';
import type {
  LKConnectionState,
  LiveKitModule,
  LiveKitRoom,
  LiveKitRoomEventCallbacks,
  LiveKitLocalAudioTrack,
  TrackPublication,
  AnimationTransformEvent,
} from './types';

// Inline worker (bundled as a blob URL)
import AnimationWorker from './animation-worker.ts?worker&inline';

// Default transition frame counts (used when protocol doesn't specify)
const DEFAULT_TRANSITION_START_FRAMES = 8;
const DEFAULT_TRANSITION_END_FRAMES = 40;

/**
 * Whether this is a WebKit-based browser (Safari, and every iOS browser).
 *
 * Only used to decide *when* the receiver transform is installed — the two
 * engines disagree on that, and neither exposes the difference as a feature to
 * detect. Chrome/Blink also reports "Safari" in its UA string, hence excluding
 * it explicitly.
 * @internal
 */
function isWebKit(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  return /Safari/.test(ua) && !/Chrome|Chromium|Edg\//.test(ua);
}

/**
 * LiveKit Provider options.
 */
export interface LiveKitProviderOptions {
  /**
   * Exact LiveKit publication track name to treat as the animation track.
   */
  animationTrackName?: string;

  /**
   * Pattern for LiveKit publication track names to treat as animation tracks.
   */
  animationTrackNamePattern?: RegExp;
}

/**
 * LiveKit Provider.
 *
 * Implements RTCProvider interface for LiveKit platform.
 * Requires a browser with RTCRtpScriptTransform support for animation data.
 *
 * @example
 * ```typescript
 * import { AvatarPlayer, LiveKitProvider } from '@spatius/avatarkit-rtc';
 *
 * const provider = new LiveKitProvider();
 * const player = new AvatarPlayer(provider, renderer);
 *
 * await player.connect({
 *   url: 'wss://your-livekit-server.com',
 *   token: 'your-token',
 * });
 * ```
 */
export class LiveKitProvider extends BaseProvider {
  /** Provider name identifier */
  readonly name = 'livekit';

  /** @internal */
  private room: LiveKitRoom | null = null;
  /** @internal */
  private livekitSDK: LiveKitModule | null = null;
  /**
   * True when the room was supplied by the host application. In this mode the
   * provider borrows the room and must never connect or disconnect it.
   * @internal
   */
  private isExternalClient = false;
  /** @internal Exact listener removals for the currently attached room. */
  private roomListenerCleanups: Array<() => void> = [];

  // Animation track subscription
  /** @internal */
  private animationCallbacks: AnimationTrackCallbacks | null = null;
  /** @internal */
  private animationWorker: Worker | null = null;
  /** @internal */
  private animationTrackIds: Set<string> = new Set();
  /**
   * Receivers already transformed, mapped to the track SID the pipeline was
   * created for. Used to remap track IDs instead of re-installing when a
   * transceiver is recycled.
   * @internal
   */
  private transformedReceivers: Map<RTCRtpReceiver, string> = new Map();
  /**
   * Receivers already evaluated by the early SRD hook.
   * @internal
   */
  private earlyEvaluatedReceivers: WeakSet<RTCRtpReceiver> = new WeakSet();
  /** @internal */
  private unregisterEarlyHandler: (() => void) | null = null;
  /** @internal */
  private animationVideoElements: Map<
    Parameters<LiveKitRoomEventCallbacks['trackSubscribed']>[0],
    HTMLMediaElement
  > = new Map();
  /**
   * The subscribed animation track, kept for its getRTCStatsReport(). Set on
   * subscribe and cleared on unsubscribe, so transport stats are only read
   * while a track is actually delivering.
   * @internal
   */
  private animationTrack:
    | Parameters<LiveKitRoomEventCallbacks['trackSubscribed']>[0]
    | null = null;

  // Audio track subscription
  /** @internal */
  private audioCallbacks: AudioTrackCallbacks | null = null;
  /** @internal */
  private audioElements: Map<string, HTMLAudioElement> = new Map();
  /** @internal */
  private awaitingAudioUnlock = false;
  /** @internal */
  private readonly audioUnlockEventNames: Array<keyof WindowEventMap> = [
    'pointerdown',
    'keydown',
    'touchstart',
  ];
  /** @internal */
  private readonly handleAudioUnlockGesture = (): void => {
    void this.ensureAudioPlaybackUnlocked('user-gesture');
  };

  // Microphone publishing
  /** @internal */
  private localAudioTrack: LiveKitLocalAudioTrack | null = null;

  constructor(private readonly options: LiveKitProviderOptions = {}) {
    super();
    this.unregisterEarlyHandler = registerEarlyReceiverHandler(
      this.handleEarlySrd,
    );
  }

  /**
   * Whether track selection is explicitly configured.
   * @internal
   */
  private hasConfiguredAnimationTrackSelector(): boolean {
    return (
      this.options.animationTrackName !== undefined ||
      this.options.animationTrackNamePattern !== undefined
    );
  }

  /**
   * Get a publication's LiveKit track name.
   * @internal
   */
  private getPublicationTrackName(
    publication: TrackPublication,
  ): string | undefined {
    const trackName = publication.trackName;
    return typeof trackName === 'string' && trackName.length > 0
      ? trackName
      : undefined;
  }

  /**
   * Check whether a track name matches the configured animation selector.
   * @internal
   */
  private matchesAnimationTrackName(trackName: string): boolean {
    if (this.options.animationTrackName === trackName) {
      return true;
    }

    const pattern = this.options.animationTrackNamePattern;
    if (pattern) {
      pattern.lastIndex = 0;
      return pattern.test(trackName);
    }

    return false;
  }

  /**
   * Check whether a LiveKit publication should be used for animation data.
   * @internal
   */
  private isSelectedAnimationPublication(
    publication: TrackPublication,
  ): boolean {
    const trackName = this.getPublicationTrackName(publication);
    if (!trackName) return false;

    return this.matchesAnimationTrackName(trackName);
  }

  private createRoom(livekit: LiveKitModule): LiveKitRoom {
    const { Room } = livekit;
    return new Room({
      // Since Chrome ~141, a receiver transform only takes effect when
      // installed before the receiver's media pipeline starts. In single
      // peer connection mode transceivers are recycled and their pipeline is
      // already running when a track arrives, which makes timely
      // installation impossible. Dual peer connection mode gives each
      // subscribed track a freshly created transceiver.
      singlePeerConnection: false,
    });
  }

  /**
   * Get the RTCRtpReceiver behind a LiveKit remote track.
   * @internal
   */
  private getTrackReceiver(track: unknown): RTCRtpReceiver | null {
    if (!track || typeof track !== 'object') return null;
    const receiver = (track as { receiver?: unknown }).receiver;
    if (!receiver || typeof receiver !== 'object' || !('track' in receiver)) {
      return null;
    }
    return receiver as RTCRtpReceiver;
  }

  /**
   * Find a remote publication's track name by its server track SID.
   * @internal
   */
  private findTrackNameByTrackSid(trackSid: string): string | undefined {
    if (!this.room) return undefined;

    for (const participant of this.room.remoteParticipants.values()) {
      const publication = participant.trackPublications.get(trackSid);
      if (publication) {
        return this.getPublicationTrackName(publication);
      }
    }
    return undefined;
  }

  /**
   * Ensure the animation worker exists.
   * @internal
   */
  private ensureAnimationWorker(): Worker {
    if (!this.animationWorker) {
      this.animationWorker = new AnimationWorker();
      this.animationWorker.addEventListener(
        'message',
        this.handleAnimationWorkerMessage,
      );
    }
    return this.animationWorker;
  }

  /**
   * Install the animation transform on a receiver. The worker consumes
   * frames that match the animation protocol and passes everything else
   * through untouched.
   * @internal
   */
  private installAnimationTransform(
    receiver: RTCRtpReceiver,
    trackSid: string,
  ): void {
    if (receiver.track?.kind !== 'video') return;
    if (this.transformedReceivers.has(receiver)) return;
    if (receiver.transform) return;
    if (typeof RTCRtpScriptTransform === 'undefined') {
      logger.warn(
        'LiveKit',
        'RTCRtpScriptTransform is not available in this browser; ' +
          'animation data will not be received.',
      );
      return;
    }

    try {
      receiver.transform = new RTCRtpScriptTransform(
        this.ensureAnimationWorker(),
        { trackId: trackSid },
      );
      this.transformedReceivers.set(receiver, trackSid);
    } catch (error) {
      logger.error('LiveKit', 'Failed to install animation transform:', error);
    }
  }

  /**
   * Early receiver hook, fired synchronously right after every
   * `setRemoteDescription` on the page resolves (before the local answer is
   * created).
   *
   * Since Chrome ~141, a receiver transform must be installed at this point;
   * installing at LiveKit's `TrackSubscribed` event is too late and the
   * animation frames would be silently dropped by the decoder instead.
   * Receivers are filtered to LiveKit-managed media (msid contains a
   * LiveKit track SID), so unrelated peer connections are untouched.
   * @internal
   */
  private readonly handleEarlySrd = (
    peerConnection: RTCPeerConnection,
    description: RTCSessionDescriptionInit,
  ): void => {
    if (!this.animationCallbacks || !this.room) return;
    // WebKit wants the opposite of Chrome here. The spec ties delivery of the
    // first frame to installing the transform in the track event; installed
    // this early, WebKit accepts the assignment and fires `rtctransform`, but
    // then never delivers a frame — the stream simply stays empty. Leaving it
    // to the TrackSubscribed path below is what works there, and Chrome is the
    // one that cannot wait that long.
    if (isWebKit()) return;

    for (const transceiver of peerConnection.getTransceivers()) {
      const receiver = transceiver.receiver;
      if (!receiver || receiver.track?.kind !== 'video') continue;
      if (this.earlyEvaluatedReceivers.has(receiver)) continue;
      if (receiver.transform) {
        this.earlyEvaluatedReceivers.add(receiver);
        continue;
      }

      // Only touch LiveKit-managed media
      const trackSid = findLiveKitTrackSid(description.sdp, transceiver.mid);
      if (!trackSid) continue;

      if (this.hasConfiguredAnimationTrackSelector()) {
        const trackName = this.findTrackNameByTrackSid(trackSid);
        if (!trackName) {
          // Publication metadata has not arrived yet - retry on the next SRD
          // instead of caching the decision.
          continue;
        }
        this.earlyEvaluatedReceivers.add(receiver);
        if (!this.matchesAnimationTrackName(trackName)) continue;
      } else {
        this.earlyEvaluatedReceivers.add(receiver);
      }

      this.installAnimationTransform(receiver, trackSid);
    }
  };

  /**
   * Load LiveKit SDK dynamically.
   * @internal
   */
  private async loadSDK(): Promise<LiveKitModule> {
    if (this.livekitSDK) {
      return this.livekitSDK;
    }

    try {
      this.livekitSDK = await import('livekit-client');
      return this.livekitSDK;
    } catch {
      throw new Error(
        '❌ Failed to load livekit-client.\n' +
          'Please ensure it is installed: pnpm add livekit-client',
      );
    }
  }

  async prepareConnection(config: RTCPrepareConnectionConfig): Promise<void> {
    logger.info('LiveKit', 'prepareConnection() called with config:', {
      hasUrl: 'url' in config,
      hasToken: 'token' in config,
    });

    if (!isLiveKitPrepareConfig(config)) {
      throw new Error(
        'LiveKitProvider requires url in prepareConnection config',
      );
    }

    if (this.isExternalClient) {
      throw new Error(
        'Cannot prepare a connection while using a host-owned LiveKit Room',
      );
    }

    const livekit = await this.loadSDK();

    if (!this.room) {
      this.room = this.createRoom(livekit);
    }

    try {
      await this.room.prepareConnection(config.url, config.token);
      logger.info('LiveKit', 'prepareConnection() completed');
    } catch (error) {
      logger.warn('LiveKit', 'prepareConnection() failed (non-fatal):', error);
    }
  }

  /**
   * Map LiveKit connection state to string.
   * @internal
   */
  private mapLiveKitConnectionState(
    livekit: LiveKitModule,
    state: LKConnectionState,
  ): string {
    const { ConnectionState: LiveKitConnectionState } = livekit;
    switch (state) {
      case LiveKitConnectionState.Disconnected:
        return 'disconnected';
      case LiveKitConnectionState.Connecting:
        return 'connecting';
      case LiveKitConnectionState.Connected:
        return 'connected';
      case LiveKitConnectionState.Reconnecting:
        return 'reconnecting';
      default:
        return 'failed';
    }
  }

  /**
   * Handle events from the animation worker.
   * LiveKit owns the worker's `onmessage` slot, so we listen via
   * `addEventListener` - both fire independently.
   * @internal
   */
  private readonly handleAnimationWorkerMessage = (
    event: MessageEvent<AnimationTransformEvent & { trackId?: string }>,
  ): void => {
    const evt = event.data;
    if (!evt || typeof evt !== 'object' || !('type' in evt)) return;
    if (!this.animationCallbacks) return;
    this.routeAnimationWorkerEvent(this.animationCallbacks, evt);
  };

  /**
   * Route an animation worker event to animation track callbacks.
   * @internal
   */
  private routeAnimationWorkerEvent(
    callbacks: AnimationTrackCallbacks,
    evt: AnimationTransformEvent,
  ): void {
    if (evt.type === 'metadata') {
      callbacks.onStreamStats({
        framesPerSec: evt.framesPerSec,
        totalFrames: evt.totalFrames ?? 0,
        framesSent: evt.framesSent ?? 0,
        framesLost: evt.framesLost ?? 0,
        framesRecovered: evt.framesRecovered ?? 0,
        framesDropped: evt.framesDropped ?? 0,
        framesOutOfOrder: evt.framesOutOfOrder ?? 0,
        framesDuplicate: evt.framesDuplicate ?? 0,
        lastRenderedSeq: evt.lastRenderedSeq ?? -1,
      });
    } else if (evt.type === 'transition') {
      callbacks.onTransition(evt.protobufData, DEFAULT_TRANSITION_START_FRAMES);
    } else if (evt.type === 'transitionEnd') {
      callbacks.onTransitionEnd(
        evt.protobufData,
        DEFAULT_TRANSITION_END_FRAMES,
      );
    } else if (evt.type === 'animation') {
      if (!evt.isIdle) {
        callbacks.onAnimationData(evt.protobufData, {
          frameSeq: evt.frameSeq,
          isStart: evt.isStart,
          isEnd: evt.isEnd,
          isIdle: evt.isIdle,
          isRecovered: evt.isRecovered,
        });
      }
    } else if (evt.type === 'idleStart') {
      callbacks.onIdleStart();
    } else if (evt.type === 'error') {
      logger.error('LiveKit', 'Animation worker error:', evt.error);
      trackEvent('rtc_worker_failed', 'error', {
        provider: 'livekit',
        description:
          typeof evt.error === 'string' ? evt.error : String(evt.error),
      });
    }
  }

  /**
   * Mark or unmark a track as an animation track with the worker.
   * @internal
   */
  private markAnimationTrack(
    track: { mediaStreamID?: string; receiver?: RTCRtpReceiver | null },
    trackSid: string,
    action: 'add' | 'remove',
  ): void {
    if (action === 'add') {
      const receiver = track.receiver ?? this.getTrackReceiver(track);
      if (receiver) {
        const existingSid = this.transformedReceivers.get(receiver);
        if (existingSid && existingSid !== trackSid) {
          // Transceiver recycled: remap the track ID instead of
          // re-installing (the transform stays on the receiver).
          this.transformedReceivers.set(receiver, trackSid);
          this.animationWorker?.postMessage({
            kind: 'updateTrackId',
            data: { oldTrackId: existingSid, newTrackId: trackSid },
          });
        } else if (!existingSid) {
          // Defensive fallback install (the early SRD hook normally beat us
          // to it); a no-op on browsers that require early installation.
          this.installAnimationTransform(receiver, trackSid);
        }
      }
      this.animationTrackIds.add(trackSid);
    } else {
      this.animationTrackIds.delete(trackSid);
    }

    this.animationWorker?.postMessage({
      kind: 'animationTrack',
      trackId: trackSid,
      action,
    });
  }

  /**
   * Add one-time user gesture listeners to recover from autoplay blocking.
   * @internal
   */
  private addAudioUnlockListeners(): void {
    if (typeof window === 'undefined' || this.awaitingAudioUnlock) {
      return;
    }

    this.awaitingAudioUnlock = true;
    for (const eventName of this.audioUnlockEventNames) {
      window.addEventListener(eventName, this.handleAudioUnlockGesture, {
        capture: true,
        passive: true,
      });
    }

    logger.warn(
      'LiveKit',
      'Audio playback blocked by autoplay policy, waiting for user interaction',
    );
  }

  /**
   * Remove user gesture listeners after playback is unlocked.
   * @internal
   */
  private removeAudioUnlockListeners(): void {
    if (typeof window === 'undefined' || !this.awaitingAudioUnlock) {
      return;
    }

    this.awaitingAudioUnlock = false;
    for (const eventName of this.audioUnlockEventNames) {
      window.removeEventListener(
        eventName,
        this.handleAudioUnlockGesture,
        true,
      );
    }
  }

  /**
   * Detect browser autoplay blocking errors from audio play/start calls.
   * @internal
   */
  private isAutoplayBlockedError(error: unknown): boolean {
    if (typeof DOMException !== 'undefined' && error instanceof DOMException) {
      return error.name === 'NotAllowedError';
    }

    if (error instanceof Error) {
      return (
        error.name === 'NotAllowedError' ||
        /didn't interact|user didn't interact|NotAllowedError/i.test(
          error.message,
        )
      );
    }

    return false;
  }

  /**
   * Try to resume all attached remote audio elements.
   * @returns true if at least one element is still blocked by autoplay policy
   * @internal
   */
  private async resumeAttachedAudioElements(reason: string): Promise<boolean> {
    let hasAutoplayBlockedElement = false;

    for (const [participantIdentity, audioElement] of this.audioElements) {
      if (!audioElement.paused) {
        continue;
      }

      try {
        await audioElement.play();
      } catch (error) {
        if (this.isAutoplayBlockedError(error)) {
          hasAutoplayBlockedElement = true;
        }
        logger.warn(
          'LiveKit',
          `Failed to resume remote audio element (${reason}) for ${participantIdentity}:`,
          error,
        );
      }
    }

    return hasAutoplayBlockedElement;
  }

  /**
   * Ensure remote audio playback is unlocked and resumed.
   * @internal
   */
  private async ensureAudioPlaybackUnlocked(reason: string): Promise<void> {
    if (!this.room) {
      return;
    }

    const room = this.room;

    if (!room.canPlaybackAudio && typeof room.startAudio === 'function') {
      try {
        await room.startAudio();
        logger.info('LiveKit', `Audio context resumed (${reason})`);
      } catch (error) {
        if (this.isAutoplayBlockedError(error)) {
          this.addAudioUnlockListeners();
        } else {
          logger.warn(
            'LiveKit',
            `Failed to call room.startAudio() (${reason}):`,
            error,
          );
        }
        return;
      }
    }

    const hasAutoplayBlockedElement =
      await this.resumeAttachedAudioElements(reason);
    if (room.canPlaybackAudio && !hasAutoplayBlockedElement) {
      this.removeAudioUnlockListeners();
    } else {
      this.addAudioUnlockListeners();
    }
  }

  async connect(config: RTCConnectionConfig): Promise<void> {
    logger.info('LiveKit', 'connect() called with config:', {
      hasUrl: 'url' in config,
      hasRoomName: 'roomName' in config,
      hasToken: 'token' in config,
      config,
    });

    if (!isLiveKitConfig(config)) {
      logger.error(
        'LiveKit',
        'Config validation failed - missing url or token',
      );
      throw new Error(
        'LiveKitProvider requires url and token in connection config',
      );
    }

    if (this.isExternalClient) {
      throw new Error(
        'Cannot call connect() while using a host-owned LiveKit Room; ' +
          'connect the Room from the host application instead',
      );
    }

    const livekitConfig: LiveKitConnectionConfig = config;
    logger.info(
      'LiveKit',
      'Config validated, connecting to:',
      livekitConfig.url,
    );

    this.removeAudioUnlockListeners();

    const livekit = await this.loadSDK();
    logger.info('LiveKit', 'SDK loaded successfully');
    if (!this.room) {
      this.room = this.createRoom(livekit);
    }

    this.setConnectionState('connecting');

    if (!this.unregisterEarlyHandler) {
      this.unregisterEarlyHandler = registerEarlyReceiverHandler(
        this.handleEarlySrd,
      );
    }

    // Setup event listeners
    this.setupEventListeners(livekit);

    try {
      logger.info('LiveKit', 'Attempting to connect to room...');
      await this.room.connect(livekitConfig.url, livekitConfig.token);
      logger.info('LiveKit', 'Room connected, state:', this.room.state);
      logger.info('LiveKit', 'Room name:', this.room.name);
      logger.info(
        'LiveKit',
        'Local participant:',
        this.room.localParticipant?.identity,
      );
      logger.info(
        'LiveKit',
        'Remote participants:',
        this.room.remoteParticipants.size,
      );

      // Log existing participants and their tracks
      this.room.remoteParticipants.forEach((participant, sid) => {
        logger.info(
          'LiveKit',
          `Remote participant: ${participant.identity}, sid: ${sid}`,
        );
        participant.trackPublications.forEach((pub, trackSid) => {
          logger.info(
            'LiveKit',
            `  Track: ${pub.trackName}, kind: ${pub.kind}, subscribed: ${pub.isSubscribed}, sid: ${trackSid}`,
          );
        });
      });

      this.setConnectionState(
        this.mapLiveKitConnectionState(livekit, this.room.state),
      );
    } catch (error) {
      logger.error('LiveKit', 'Connection failed:', error);
      this.setConnectionState('failed');
      this.emit('error', error as Error);
      throw error;
    }
  }

  /**
   * Setup LiveKit room event listeners
   * @internal
   */
  private setupEventListeners(livekit: LiveKitModule): void {
    if (!this.room) return;

    const room = this.room;
    const { RoomEvent, Track } = livekit;
    this.removeRoomEventListeners();

    const on = <Event extends keyof LiveKitRoomEventCallbacks>(
      event: Event,
      listener: LiveKitRoomEventCallbacks[Event],
    ): void => {
      room.on(event, listener);
      this.roomListenerCleanups.push(() => room.off(event, listener));
    };

    // Connected
    on(RoomEvent.Connected, () => {
      this.setConnectionState('connected');
      if (!this.isExternalClient) {
        this.emit('connected');
      }

      if (!this.isExternalClient && !room.canPlaybackAudio) {
        this.addAudioUnlockListeners();
      }
    });

    // Participant connected
    on(RoomEvent.ParticipantConnected, (participant) => {
      logger.info('LiveKit', `Participant connected: ${participant.identity}`);
    });

    // Participant disconnected
    on(RoomEvent.ParticipantDisconnected, (participant) => {
      logger.info(
        'LiveKit',
        `Participant disconnected: ${participant.identity}`,
      );
    });

    // Track published
    on(RoomEvent.TrackPublished, (publication, participant) => {
      logger.info('LiveKit', 'TrackPublished:', {
        trackName: publication.trackName,
        kind: publication.kind,
        participant: participant.identity,
      });
    });

    // Disconnected
    on(RoomEvent.Disconnected, () => {
      this.cleanup();
      this.setConnectionState('disconnected');
      if (!this.isExternalClient) {
        this.emit('disconnected');
      }
    });

    on(RoomEvent.AudioPlaybackStatusChanged, (canPlaybackAudio) => {
      if (this.isExternalClient) return;

      logger.info(
        'LiveKit',
        `Audio playback status changed: ${canPlaybackAudio ? 'enabled' : 'blocked'}`,
      );

      if (canPlaybackAudio) {
        void this.ensureAudioPlaybackUnlocked('audio-playback-status-changed');
      } else {
        this.addAudioUnlockListeners();
      }
    });

    // Track subscribed
    on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
      logger.info('LiveKit', 'TrackSubscribed:', {
        kind: track.kind,
        trackName: publication.trackName,
        participant: participant.identity,
      });

      if (track.kind === Track.Kind.Audio) {
        // A host-owned room also owns its audio playback. Attaching another
        // element here would play the same remote audio twice when the host
        // has already attached the track.
        if (this.isExternalClient) return;

        logger.info('LiveKit', 'Audio track received, attaching...');
        // Audio track
        const audioElement = track.attach() as HTMLAudioElement;
        audioElement.id = `audio-${participant.identity}`;
        audioElement.autoplay = true;
        audioElement.controls = false;
        audioElement.style.display = 'none';

        const previousAudioElement = this.audioElements.get(
          participant.identity,
        );
        if (previousAudioElement && previousAudioElement !== audioElement) {
          previousAudioElement.remove();
        }

        if (typeof document !== 'undefined') {
          document.body.appendChild(audioElement);
          logger.info('LiveKit', 'Audio element appended to body');
        }

        this.audioElements.set(participant.identity, audioElement);
        void this.ensureAudioPlaybackUnlocked('audio-track-subscribed');

        if (this.audioCallbacks) {
          this.audioCallbacks.onAudioReceived?.(participant);
        }
      } else if (track.kind === Track.Kind.Video) {
        const shouldUseAsAnimationTrack =
          !this.hasConfiguredAnimationTrackSelector() ||
          this.isSelectedAnimationPublication(publication);

        if (!shouldUseAsAnimationTrack) {
          return;
        }

        if (this.animationVideoElements.has(track)) {
          return;
        }

        logger.info(
          'LiveKit',
          'Video track received, setting up animation extraction...',
        );

        // Mark + install so the worker consumes this track's animation
        // frames. Prefer the server track SID (matches the early hook).
        this.markAnimationTrack(
          track,
          publication.trackSid ?? track.mediaStreamID,
          'add',
        );

        // Held for getTransportStats(); the report comes off this track's
        // receiver.
        this.animationTrack = track;

        // Attach to hidden video element to keep data flowing
        const videoElement = track.attach() as HTMLVideoElement;
        videoElement.id = `video-animation-${participant.identity}`;
        videoElement.muted = true;
        videoElement.autoplay = true;
        videoElement.style.display = 'none';
        if (typeof document !== 'undefined') {
          document.body.appendChild(videoElement);
          this.animationVideoElements.set(track, videoElement);
          videoElement.play().catch(() => {});
        }
      }
    });

    // Track unsubscribed
    on(RoomEvent.TrackUnsubscribed, (track, _publication, participant) => {
      if (track.kind === Track.Kind.Audio) {
        const audioElement = this.audioElements.get(participant.identity);
        if (audioElement) {
          track.detach(audioElement);
          audioElement.remove();
        }
        this.audioElements.delete(participant.identity);

        if (this.audioCallbacks) {
          this.audioCallbacks.onAudioLost?.(participant);
        }
      } else if (track.kind === Track.Kind.Video) {
        this.markAnimationTrack(
          track,
          _publication.trackSid ?? track.mediaStreamID,
          'remove',
        );

        const videoElement = this.animationVideoElements.get(track);
        if (videoElement) {
          track.detach(videoElement);
          videoElement.remove();
          this.animationVideoElements.delete(track);
        }
        if (this.animationTrack === track) {
          this.animationTrack = null;
        }
      }
    });

    // Connection state changed
    on(RoomEvent.ConnectionStateChanged, (state) => {
      this.setConnectionState(this.mapLiveKitConnectionState(livekit, state));
    });
  }

  async disconnect(): Promise<void> {
    if (this.isExternalClient) {
      this.detachExternalClient();
      return;
    }

    this.cleanup();
    if (this.unregisterEarlyHandler) {
      this.unregisterEarlyHandler();
      this.unregisterEarlyHandler = null;
    }
    if (this.room) {
      this.room.disconnect();
      this.room = null;
    }
    this.removeRoomEventListeners();
    this.setConnectionState('disconnected');
  }

  /**
   * Attach to a host-owned LiveKit Room instead of creating one.
   *
   * The provider borrows the Room only: the host remains responsible for
   * constructing it, calling `connect()` / `disconnect()`, and managing remote
   * audio playback. The provider adds only the listeners and hidden video
   * element needed for avatar animation extraction.
   *
   * This must be called before the host calls `room.connect()`. The Room must
   * be constructed with `{ singlePeerConnection: false }`, which gives each
   * subscribed track a fresh receiver early enough for the animation transform
   * to be installed reliably on current Chromium browsers.
   *
   * @example
   * ```typescript
   * const room = new Room({ singlePeerConnection: false });
   * await player.attach(room);
   * await room.connect(url, token);
   * ```
   *
   * @param room - The host-owned LiveKit Room to borrow.
   * @throws Error if another room is already set or the room has started
   * connecting.
   */
  async attachExternalClient(room: LiveKitRoom): Promise<void> {
    if (this.room) {
      throw new Error(
        'Client already set; call disconnect()/detachExternalClient() first',
      );
    }

    const livekit = await this.loadSDK();
    if (
      !room ||
      typeof room.on !== 'function' ||
      typeof room.off !== 'function'
    ) {
      throw new Error('attachExternalClient() requires a LiveKit Room');
    }
    if (room.state !== livekit.ConnectionState.Disconnected) {
      throw new Error(
        'attachExternalClient() must be called before room.connect() so the ' +
          'animation receiver transform can be installed in time',
      );
    }
    if (room.options?.singlePeerConnection !== false) {
      throw new Error(
        'A host-owned LiveKit Room must be created with ' +
          '{ singlePeerConnection: false } for avatar animation extraction',
      );
    }

    this.room = room;
    this.isExternalClient = true;
    if (!this.unregisterEarlyHandler) {
      this.unregisterEarlyHandler = registerEarlyReceiverHandler(
        this.handleEarlySrd,
      );
    }
    this.setupEventListeners(livekit);
    this.setConnectionState(
      this.mapLiveKitConnectionState(livekit, room.state),
    );
  }

  /**
   * Detach from a host-owned LiveKit Room without disconnecting it or removing
   * any listeners/media elements owned by the host.
   */
  detachExternalClient(): void {
    if (!this.isExternalClient) return;

    this.removeRoomEventListeners();
    this.cleanup();
    if (this.unregisterEarlyHandler) {
      this.unregisterEarlyHandler();
      this.unregisterEarlyHandler = null;
    }
    this.room = null;
    this.isExternalClient = false;
    this.setConnectionState('disconnected');
  }

  /** @internal */
  private removeRoomEventListeners(): void {
    for (const removeListener of this.roomListenerCleanups) {
      removeListener();
    }
    this.roomListenerCleanups = [];
  }

  getConnectionState(): string {
    if (!this.room) {
      return 'disconnected';
    }
    return this.connectionState;
  }

  /** @internal */
  async subscribeAnimationTrack(
    callbacks: AnimationTrackCallbacks,
  ): Promise<void> {
    this.animationCallbacks = callbacks;

    // If room is already connected, check for existing tracks
    if (this.room) {
      const livekit = await this.loadSDK();
      const { ConnectionState: LiveKitConnectionState, Track } = livekit;

      if (this.room.state === LiveKitConnectionState.Connected) {
        // Check for existing video tracks that might be animation tracks
        for (const participant of this.room.remoteParticipants.values()) {
          // Get video track publications (handle different SDK versions)
          const publications =
            participant.videoTrackPublications ?? participant.trackPublications;
          for (const publication of publications.values()) {
            if (publication.kind !== Track.Kind.Video) continue;

            const shouldUseAsAnimationTrack =
              !this.hasConfiguredAnimationTrackSelector() ||
              this.isSelectedAnimationPublication(publication);

            const track = publication.track;
            if (shouldUseAsAnimationTrack && track) {
              this.markAnimationTrack(
                track,
                publication.trackSid ?? track.mediaStreamID,
                'add',
              );
            }
          }
        }
      }
    }
  }

  /** @internal */
  async unsubscribeAnimationTrack(): Promise<void> {
    this.animationCallbacks = null;

    if (this.animationWorker) {
      for (const trackId of this.animationTrackIds) {
        this.animationWorker.postMessage({
          kind: 'animationTrack',
          trackId,
          action: 'remove',
        });
      }
    }
    this.animationTrackIds.clear();
    this.animationTrack = null;
  }

  /**
   * Transport-level view of the inbound animation stream, read from the
   * track's own RTCStatsReport.
   *
   * `getRTCStatsReport()` is LiveKit's public accessor for the receiver's
   * stats — no reaching into room internals, which is what the early-transform
   * rework had to move away from.
   *
   * The loss rate is derived here rather than taken from a provider field
   * (LiveKit has none) and is cumulative for the connection: callers wanting a
   * windowed rate should difference successive samples.
   * @internal
   */
  async getTransportStats(): Promise<TransportStats | null> {
    const track = this.animationTrack;
    if (!track || typeof track.getRTCStatsReport !== 'function') return null;

    let report: RTCStatsReport | undefined;
    try {
      report = await track.getRTCStatsReport();
    } catch {
      // Stats are best-effort: a closed or renegotiating receiver throws, and
      // that is not worth failing a sampling tick over.
      return null;
    }
    if (!report) return null;

    const stats: TransportStats = {};
    report.forEach((entry: Record<string, unknown>) => {
      if (entry.type === 'inbound-rtp') {
        if (typeof entry.packetsLost === 'number') {
          stats.packetsLost = entry.packetsLost;
        }
        if (typeof entry.packetsReceived === 'number') {
          stats.packetsReceived = entry.packetsReceived;
        }
        // Seconds in the spec, milliseconds everywhere in our telemetry.
        if (typeof entry.jitter === 'number') {
          stats.jitterMs = entry.jitter * 1000;
        }
      } else if (entry.type === 'candidate-pair') {
        // Only the pair actually carrying media has a round-trip time worth
        // reading; the others are leftovers from ICE negotiation.
        const isActive =
          entry.state === 'succeeded' &&
          (entry.nominated === true || entry.selected === true);
        if (isActive && typeof entry.currentRoundTripTime === 'number') {
          stats.rttMs = entry.currentRoundTripTime * 1000;
        }
      }
    });

    if (
      stats.packetsLost !== undefined &&
      stats.packetsReceived !== undefined
    ) {
      const total = stats.packetsLost + stats.packetsReceived;
      if (total > 0) {
        stats.lossRatePct = (stats.packetsLost / total) * 100;
      }
    }

    return Object.keys(stats).length > 0 ? stats : null;
  }

  /** @internal */
  async subscribeAudioTrack(callbacks: AudioTrackCallbacks): Promise<void> {
    this.audioCallbacks = callbacks;
    // Audio tracks are automatically handled in TrackSubscribed event
  }

  /** @internal */
  async unsubscribeAudioTrack(): Promise<void> {
    this.audioCallbacks = null;
    this.audioElements.forEach((el) => {
      el.remove();
    });
    this.audioElements.clear();
  }

  async publishAudioTrack(track?: MediaStreamTrack): Promise<void> {
    if (!this.room) {
      throw new Error('Not connected to room');
    }

    try {
      const livekit = await this.loadSDK();
      const { createLocalAudioTrack, Track: TrackEnum } = livekit;

      // If track is provided, use it; otherwise create from microphone
      if (track) {
        await this.room.localParticipant.publishTrack(track, {
          name: 'user-microphone',
          source: TrackEnum.Source.Microphone,
          red: false,
        });
      } else {
        // Create and publish microphone track
        const localTrack = await createLocalAudioTrack({
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        });

        this.localAudioTrack = localTrack;

        await this.room.localParticipant.publishTrack(localTrack, {
          name: 'user-microphone',
          source: TrackEnum.Source.Microphone,
          red: false,
        });
      }
    } catch (error) {
      logger.error('LiveKit', 'Failed to publish audio track:', error);
      throw error;
    }
  }

  async unpublishAudioTrack(): Promise<void> {
    if (!this.room || !this.localAudioTrack) {
      return;
    }

    await this.room.localParticipant.unpublishTrack(this.localAudioTrack);
    this.localAudioTrack.stop();
    this.localAudioTrack = null;
  }

  /**
   * Get the native LiveKit Room instance.
   *
   * Allows advanced users to access LiveKit-specific features
   * not exposed through the unified API.
   *
   * @returns The LiveKit Room instance, or null if not connected
   *
   * @example
   * ```typescript
   * const room = provider.getNativeClient();
   * if (room) {
   *   // Access LiveKit-specific features
   *   console.log('Participants:', room.remoteParticipants.size);
   * }
   * ```
   */
  getNativeClient(): LiveKitRoom | null {
    return this.room;
  }

  /**
   * Cleanup resources
   * @internal
   */
  private cleanup(): void {
    this.removeAudioUnlockListeners();

    // Cleanup audio elements
    this.audioElements.forEach((el) => {
      el.remove();
    });
    this.audioElements.clear();

    // Cleanup hidden animation video elements
    this.animationVideoElements.forEach((element, track) => {
      track.detach(element);
      element.remove();
    });
    this.animationVideoElements.clear();
    this.animationTrack = null;

    // Terminate the animation worker and drop transform registrations.
    if (this.animationWorker) {
      this.animationWorker.terminate();
      this.animationWorker = null;
    }
    this.animationTrackIds.clear();
    this.transformedReceivers.clear();
    this.earlyEvaluatedReceivers = new WeakSet();

    // Stop local audio track
    if (this.localAudioTrack) {
      this.localAudioTrack.stop();
      this.localAudioTrack = null;
    }
  }
}
